import { CSSProperties, useContext, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { FieldItem } from '@april9/stack9-sdk';
import { Editor, IAllProps as EditorProps } from '@tinymce/tinymce-react';

import { AppContext } from 'contexts/AppContext';
import cx from 'classnames';

import { useEntityContext } from 'contexts/EntityContext';

import FieldLabel from './FieldLabel';

const isHiddenStyles: CSSProperties = {
  position: 'absolute',
  left: '-9999px',
  top: '-9999px',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  visibility: 'hidden',
  zIndex: -999
}

export type HtmlFieldProps = {
  name: string;
  fieldItem: FieldItem;
  defaultValue?: any;
  entityKey: string;
};

const HtmlField = ({
  name,
  defaultValue,
  fieldItem,
  entityKey,
}: HtmlFieldProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const { insertFileUpload } = useEntityContext();
  const {
    state: { creds },
  } = useContext(AppContext);

  const [isEditorReady, setIsEditorReady] = useState(false);

  const { ui_component_options, description } = fieldItem;

  const readOnly = ui_component_options?.readOnly || false;

  const editorInitProps = useMemo(() => {
    const images_upload_handler: NonNullable<
      EditorProps['init']
    >['images_upload_handler'] = async (blobInfo, progressFn) => {
      try {
        const formData = new FormData();
        formData.append('files', blobInfo.blob());

        const response = await insertFileUpload(
          entityKey,
          name,
          formData,
          _event => {
            progressFn(Math.round((_event.loaded / _event.total) * 100));
          },
        );

        if (!response?.url) {
          throw new Error('Image upload failed - no URL returned');
        }

        return response.url;
      } catch (error) {
        throw new Error(`Image upload error: ${error}`);
      }
    };

    return {
      height: 250,
      images_upload_handler: images_upload_handler,
      content_css: '/htmlEditor.css',
      plugins: [
        'advlist',
        'autolink',
        'lists',
        'link',
        'image',
        'charmap',
        'preview',
        'anchor',
        'searchreplace',
        'visualblocks',
        'code',
        'fullscreen',
        'insertdatetime',
        'media',
        'table',
        'help',
        'wordcount',
      ],
      toolbar:
        'undo redo | blocks | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help | image | code',
      paste_as_text: false,
      paste_block_drop: false,
      automatic_uploads: false,
      extended_valid_elements:
        'img[class|src|alt|title|width|height|loading=lazy]',
      convert_urls: true,
      relative_urls: false,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityKey, name]);

  return (
    <FieldLabel fieldItem={fieldItem}>
      <div data-cy={name} className={cx({ 'is-invalid': errors[name] })}>
        <Controller
          control={control}
          name={name}
          defaultValue={defaultValue}
          render={({ field }) => (
            <>
              {!isEditorReady && (
                <div style={{ height: 250, background: '#f5f5f5', borderRadius: 4 }} />
              )}
              <div style={isEditorReady ? {} : isHiddenStyles}>
                <Editor
                  apiKey={creds?.tinymce?.apiKey || ''}
                  value={field.value}
                  disabled={readOnly}
                  onEditorChange={field.onChange}
                  onBlur={field.onBlur}
                  onInit={() => setIsEditorReady(true)}
                  init={editorInitProps}
                />
              </div>
            </>
          )}
        />

        {description && (
          <small className="form-text text-muted">{description}</small>
        )}
      </div>
    </FieldLabel>
  );
};

export default HtmlField;
